import React, { useState, useEffect, useCallback } from 'react';
import api, { uploadMedia } from '../utils/api';
import ImageCropperModal from './ImageCropperModal';
import './EditStaffDialog.css';

const EditStaffDialog = ({ staff, subunits, onClose, onEdit }) => {
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    email: '',
    designation: '',
    phoneNumber: '',
    facultyName: '',
    department_ref: '',
    dp: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [adminUnits, setAdminUnits] = useState([]);
  const [availableSubunits, setAvailableSubunits] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  const fetchAdminUnits = async () => {
    try {
      const response = await api.get('/admin-units');
      if (response.data.success) {
        setAdminUnits(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch admin units:', err);
    }
  };

  const fetchSubunitsForAdminUnit = useCallback(async (adminUnitName) => {
    try {
      if (!adminUnitName || adminUnits.length === 0) {
        return;
      }

      const adminUnit = adminUnits.find(unit => unit.name === adminUnitName);
      if (adminUnit) {
        const response = await api.get(`/admin-subunits?administration_ref=${adminUnit._id}`);
        if (response.data.success) {
          setAvailableSubunits(response.data.data);
        }
      } else {
        setAvailableSubunits([]);
      }
    } catch (err) {
      console.error('Failed to fetch subunits for admin unit:', err);
      setAvailableSubunits([]);
    }
  }, [adminUnits]);

  const findAdminUnitForStaff = useCallback(async (departmentRef) => {
    try {
      if (!departmentRef || adminUnits.length === 0) {
        return null;
      }

      // Check each admin unit to find which one contains this subunit
      for (const adminUnit of adminUnits) {
        try {
          const response = await api.get(`/admin-subunits?administration_ref=${adminUnit._id}`);
          if (response.data.success) {
            const subunits = response.data.data;
            const currentSubunit = subunits.find(subunit => subunit._id === departmentRef);
            if (currentSubunit) {
              return adminUnit.name;
            }
          }
        } catch (err) {
          console.error('Error checking admin unit:', adminUnit.name, err);
        }
      }
      
      return null;
    } catch (err) {
      console.error('Failed to find admin unit for staff:', err);
      return null;
    }
  }, [adminUnits]);

  useEffect(() => {
    if (staff) {
      const newFormData = {
        name: staff.name || '',
        nameEn: staff.nameEn || '',
        email: staff.email || '',
        designation: staff.designation || '',
        phoneNumber: staff.phoneNumber || '',
        facultyName: staff.facultyName || '',
        department_ref: staff.department_ref || '',
        dp: staff.dp || ''
      };
      setFormData(newFormData);
      setSelectedFile(null);
    }
    fetchAdminUnits();
  }, [staff]);

  // Auto-find and set the correct admin unit when staff and admin units are available
  useEffect(() => {
    const autoSelectAdminUnit = async () => {
      if (staff && staff.department_ref && adminUnits.length > 0 && !formData.facultyName) {
        const adminUnitName = await findAdminUnitForStaff(staff.department_ref);
        if (adminUnitName) {
          setFormData(prev => ({
            ...prev,
            facultyName: adminUnitName
          }));
        }
      }
    };
    
    autoSelectAdminUnit();
  }, [staff, adminUnits, formData.facultyName, findAdminUnitForStaff]);

  useEffect(() => {
    if (formData.facultyName && adminUnits.length > 0) {
      fetchSubunitsForAdminUnit(formData.facultyName);
    }
  }, [formData.facultyName, adminUnits, fetchSubunitsForAdminUnit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      };
      
      // If faculty name changes, clear department selection and available subunits
      if (name === 'facultyName') {
        updated.department_ref = '';
        setAvailableSubunits([]);
      }
      
      return updated;
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file.');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB.');
        return;
      }
      
      setSelectedFile(file);
      setError('');
      // Open cropper modal
      setShowCropper(true);
    }
  };

  const handleCropComplete = (croppedBlob) => {
    setShowCropper(false);
    
    // Create a File object from the blob
    const croppedFile = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
    setSelectedFile(croppedFile);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(prev => ({
        ...prev,
        dp: e.target.result
      }));
    };
    reader.readAsDataURL(croppedBlob);
  };

  const handleRemoveDP = () => {
    setFormData(prev => ({
      ...prev,
      dp: ''
    }));
    setSelectedFile(null);
    const fileInput = document.getElementById('dp-upload-staff-edit');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    if (!formData.designation.trim()) {
      setError('Designation is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Upload DP if selected
      let dpUrl = formData.dp; // Keep existing URL if no new file
      
      if (selectedFile) {
        setUploading(true);
        const uploadResult = await uploadMedia(selectedFile);
        setUploading(false);
        if (!uploadResult.success) {
          setError(uploadResult.error || 'Failed to upload image');
          setLoading(false);
          return;
        }
        dpUrl = uploadResult.url;
      }

      // Prepare data with uploaded DP URL
      const submitData = {
        ...formData,
        dp: dpUrl || null
      };

      await onEdit(staff._id, submitData);
      
      // Clear selected file after successful submission
      setSelectedFile(null);
      
      // Close dialog on success
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!staff) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>Edit Staff Member</h3>
          <button 
            className="close-button" 
            onClick={handleClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}



          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter full name"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="nameEn">English Name</label>
            <input
              type="text"
              id="nameEn"
              name="nameEn"
              value={formData.nameEn}
              onChange={handleInputChange}
              placeholder="Enter English name"
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter email address"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="designation">Designation *</label>
            <input
              type="text"
              id="designation"
              name="designation"
              value={formData.designation}
              onChange={handleInputChange}
              placeholder="Enter designation"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder="Enter phone number"
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="facultyName">Faculty Name *</label>
            <select
              id="facultyName"
              name="facultyName"
              value={formData.facultyName}
              onChange={handleInputChange}
              required
              disabled={loading}
              className="form-input"
            >
              <option value="">Select an admin unit</option>
              {adminUnits.map((unit) => (
                <option key={unit._id} value={unit.name}>
                  {unit.name} ({unit.nameEn})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="department_ref">Subunit *</label>
            <select
              id="department_ref"
              name="department_ref"
              value={formData.department_ref}
              onChange={handleInputChange}
              required
              disabled={loading || !formData.facultyName}
              className="form-input"
            >
              <option value="">Select a subunit</option>
              {availableSubunits.map((subunit) => (
                <option key={subunit._id} value={subunit._id}>
                  {subunit.name} ({subunit.nameEn})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="dp-upload-staff-edit">Display Picture</label>
            <div className="avatar-upload-container">
              <div 
                className={`circular-avatar ${selectedFile || formData.dp ? 'has-content' : ''}`}
                onClick={() => !loading && !uploading && document.getElementById('dp-upload-staff-edit').click()}
              >
                {formData.dp ? (
                  <img src={formData.dp} alt="Profile" className="avatar-image" loading="lazy" decoding="async" />
                ) : selectedFile ? (
                  <img 
                    src={URL.createObjectURL(selectedFile)} 
                    alt="Profile" 
                    className="avatar-image preview" 
                  />
                ) : (
                  <div className="avatar-placeholder">
                    <span className="avatar-icon">📷</span>
                    <span className="avatar-text">Click to upload</span>
                  </div>
                )}
              </div>
              
              <input
                type="file"
                id="dp-upload-staff-edit"
                accept="image/*"
                onChange={handleFileSelect}
                disabled={loading || uploading}
                className="file-input-hidden"
                style={{ display: 'none' }}
              />
              
              {(selectedFile || formData.dp) && (
                <div className="avatar-actions">
                  {selectedFile && (
                    <span className="upload-info">
                      Will be uploaded when you save
                    </span>
                  )}
                  {formData.dp && (
                    <button 
                      type="button" 
                      onClick={handleRemoveDP}
                      className="remove-button"
                      disabled={loading}
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="dialog-actions">
            <button
              type="button"
              onClick={handleClose}
              className="cancel-button"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={loading || !formData.name.trim() || !formData.email.trim() || !formData.designation.trim() || !formData.facultyName || !formData.department_ref}
            >
              {loading ? 'Updating...' : 'Update Staff'}
            </button>
          </div>
        </form>

        {showCropper && selectedFile && (
          <ImageCropperModal
            imageFile={selectedFile}
            onCropComplete={handleCropComplete}
            onCancel={() => {
              setShowCropper(false);
              setSelectedFile(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default EditStaffDialog;
